import type { EventRecord } from "@/types/records";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowUpRight,
  Calendar,
  MapPin,
  Plus,
  Search,
  Trash2,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ProducerEventsSkeleton } from "@/components/producer/ProducerPageSkeleton";
import { EventImage } from "@/components/EventImage";
import { EventStatusBadge } from "@/components/EventStatusBadge";
import { EmptyState } from "@/components/EmptyState";
import { Progress } from "@/components/ui/progress";
import { SearchInput } from "@/components/ui/search-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { useOrganization } from "@/contexts/OrganizationContext";
import {
  getAllProducerEvents,
  deleteEvent,
  canDeleteEvents,
  type OrganizationRole,
} from "@/lib/api-producer";
import { formatEventDateTimeRange, resolveEventTimeZone } from "@/lib/event-timezone";
import { ForceDeleteEventDialog } from "@/components/admin/ForceDeleteEventDialog";
import { useToast } from "@/hooks/use-toast";
import {
  panelAccentDot,
  panelEase,
  panelPage,
  panelPill,
  panelRow,
  panelTitle,
} from "@/lib/panel-ui";
import { cn } from "@/lib/utils";

export default function ProducerEvents() {
  const { t } = useTranslation("producerDashboard");
  const { user } = useAuth();
  const { activeContext, setActiveContext, organizations } = useOrganization();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const reduceMotion = useReducedMotion();

  const [search, setSearch] = useState("");
  const [scopeFilter, setScopeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<"all" | "published" | "draft" | "finished">("all");
  const [eventToDelete, setEventToDelete] = useState<EventRecord | null>(null);

  const enter = (i: number) =>
    reduceMotion
      ? {}
      : {
          initial: { opacity: 0, transform: "translateY(10px) scale(0.98)" },
          animate: { opacity: 1, transform: "translateY(0) scale(1)" },
          transition: { duration: 0.45, delay: i * 0.04, ease: panelEase },
        };

  const orgsKey = (organizations || []).map((o) => o.org_id).sort().join(",");

  // Busca todos os eventos: Pessoais + de todas as Organizações que o usuário participa (paralelo e em lote)
  const { data: allEvents = [], isLoading } = useQuery({
    queryKey: ["all-producer-events", user?.id, orgsKey],
    queryFn: () =>
      getAllProducerEvents(
        user!.id,
        organizations,
        t("events.sections.defaultOrgName"),
        t("events.sections.personalFallback"),
      ),
    enabled: !!user?.id,
    placeholderData: (previousData) => previousData,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });

  const deleteMutation = useMutation({
    mutationFn: ({ eventId, producerId }: { eventId: string; producerId: string }) =>
      deleteEvent(eventId, producerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all-producer-events"] });
      queryClient.invalidateQueries({ queryKey: ["producer-events"] });
      queryClient.invalidateQueries({ queryKey: ["producer-dashboard"] });
      toast({ title: t("events.toast.deleteSuccess") });
    },
    onError: (err) =>
      toast({
        title: t("events.toast.deleteErrorTitle"),
        description: err.message,
        variant: "destructive",
      }),
  });

  const canUserDelete = (event) => {
    if (event.producer_id === user?.id) return true;
    if (event.organization_id && organizations) {
      const org = organizations.find((o) => o.org_id === event.organization_id);
      if (org?.org_role) {
        return canDeleteEvents(org.org_role as OrganizationRole);
      }
    }
    return false;
  };

  const filteredEvents = useMemo(() => {
    if (!allEvents) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return allEvents.filter((event) => {
      // 1. Filtro de Origem
      if (scopeFilter === "personal" && event.organization_id) return false;
      if (
        scopeFilter !== "all" &&
        scopeFilter !== "personal" &&
        event.organization_id !== scopeFilter
      )
        return false;

      // 2. Filtro de Status
      const eventEndDate = new Date(event.end_date);
      const isFinished = eventEndDate < today;

      let matchesStatus = true;
      if (statusFilter === "finished") {
        matchesStatus = isFinished;
      } else if (statusFilter !== "all") {
        matchesStatus = event.status === statusFilter && !isFinished;
      }

      // 3. Busca por texto
      const searchTerm = search.trim().toLowerCase();
      const matchesSearch = !searchTerm
        ? true
        : [event.title, event.venue_city, event.slug, event._orgName]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            .includes(searchTerm);

      return matchesStatus && matchesSearch;
    });
  }, [allEvents, search, statusFilter, scopeFilter]);

  // Agrupamento em seções por Origem (Pessoal vs Organizações)
  const eventSections = useMemo(() => {
    if (scopeFilter !== "all") {
      const title =
        scopeFilter === "personal"
          ? t("events.sections.personal")
          : organizations.find((o) => o.org_id === scopeFilter)?.org_name ||
            t("events.sections.defaultOrgName");
      const isOrg = scopeFilter !== "personal";
      return [{ id: scopeFilter, title, isOrg, events: filteredEvents }];
    }

    const personalList = filteredEvents.filter((e) => !e.organization_id);
    const orgSectionsMap = new Map<
      string,
      { id: string; title: string; isOrg: boolean; events: EventRecord[] }
    >();

    filteredEvents.forEach((evt) => {
      if (evt.organization_id) {
        if (!orgSectionsMap.has(evt.organization_id)) {
          orgSectionsMap.set(evt.organization_id, {
            id: evt.organization_id,
            title: evt._orgName || t("events.sections.defaultOrgName"),
            isOrg: true,
            events: [],
          });
        }
        orgSectionsMap.get(evt.organization_id)!.events.push(evt);
      }
    });

    const sections: { id: string; title: string; isOrg: boolean; events: EventRecord[] }[] = [];

    if (personalList.length > 0) {
      sections.push({
        id: "personal",
        title: t("events.sections.personal"),
        isOrg: false,
        events: personalList,
      });
    }

    orgSectionsMap.forEach((sec) => {
      sections.push(sec);
    });

    if (sections.length === 0 && filteredEvents.length > 0) {
      sections.push({
        id: "all",
        title: t("events.sections.allEvents"),
        isOrg: false,
        events: filteredEvents,
      });
    }

    return sections;
  }, [filteredEvents, scopeFilter, organizations, t]);

  const handleCardClick = (event) => {
    // Sincronizar contexto ativo automaticamente ao clicar no evento
    if (event.organization_id) {
      const org = organizations.find((o) => o.org_id === event.organization_id);
      if (org) {
        setActiveContext({
          type: "organization",
          organizationId: org.org_id,
          organizationName: org.org_name,
          organizationSlug: org.org_slug,
          organizationLogo: org.org_logo,
        });
      }
    } else {
      setActiveContext({
        type: "personal",
        organizationId: null,
        organizationName: null,
        organizationSlug: null,
        organizationLogo: null,
      });
    }
    navigate(`/produtor/eventos/${event.id}/painel`);
  };

  const personalCount = allEvents.filter((e) => !e.organization_id).length;

  const scopeTabs = [
    { id: "all", label: t("events.tabs.all"), count: allEvents.length },
    { id: "personal", label: t("events.tabs.personal"), count: personalCount },
    ...organizations.map((org) => ({
      id: org.org_id,
      label: org.org_name,
      count: allEvents.filter((e) => e.organization_id === org.org_id).length,
    })),
  ];

  const statusOptions: { value: typeof statusFilter; label: string }[] = [
    { value: "all", label: t("events.status.all") },
    { value: "published", label: t("events.status.published") },
    { value: "draft", label: t("events.status.draft") },
    { value: "finished", label: t("events.status.finished") },
  ];

  if (isLoading && allEvents.length === 0) {
    return <ProducerEventsSkeleton />;
  }

  return (
    <div className={panelPage}>
      {/* Header Solto & Ação Principal */}
      <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className={panelTitle}>{t("events.header.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("events.header.subtitle")}
          </p>
        </div>

        <Button asChild size="sm" className={panelPill}>
          <Link to="/produtor/eventos/novo" className="gap-1.5">
            <Plus className="h-4 w-4" />
            {t("events.header.createButton")}
          </Link>
        </Button>
      </header>

      {/* Filtros em Pílula */}
      <div className="space-y-3.5">
        {/* Origem — Tabs Pílula */}
        <div className="flex items-center gap-1.5 overflow-x-auto rounded-full bg-secondary/70 p-1 scrollbar-none">
          {scopeTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setScopeFilter(tab.id)}
              className={cn(
                "flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-medium transition-colors duration-150 shrink-0",
                scopeFilter === tab.id
                  ? "bg-background text-foreground shadow-sm font-semibold"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <span className="max-w-[140px] truncate">{tab.label}</span>
              <span
                className={cn(
                  "rounded-full px-1.5 py-0.2 text-[10px] tabular-nums",
                  scopeFilter === tab.id
                    ? "bg-secondary text-foreground"
                    : "text-muted-foreground/80",
                )}
              >
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Busca e Filtro de Status */}
        <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center">
          <div className="flex-1 sm:max-w-md">
            <SearchInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("events.search.placeholder")}
              className="h-11 rounded-2xl border-border/60 bg-card text-sm shadow-none focus-visible:ring-1 focus-visible:ring-primary/30"
            />
          </div>

          <Select
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}
          >
            <SelectTrigger className="h-11 w-full sm:w-48 rounded-2xl border-border/60 bg-card text-xs font-medium shadow-none hover:bg-secondary/40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-2xl border-border/60">
              {statusOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value} className="rounded-xl text-xs">
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Lista de Eventos */}
      {!allEvents || allEvents.length === 0 ? (
        <EmptyState
          icon={<Plus className="h-10 w-10 text-muted-foreground" />}
          title={t("events.empty.title")}
          description={t("events.empty.description")}
          actionLabel={t("events.empty.actionLabel")}
          onAction={() => navigate("/produtor/eventos/novo")}
        />
      ) : filteredEvents.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-[28px] border border-dashed border-border/70 p-12 text-center">
          <p className="text-sm font-medium text-foreground">{t("events.noResults")}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Tente buscar com outros termos ou altere os filtros selecionados.
          </p>
        </div>
      ) : (
        <div className="space-y-7">
          {eventSections.map((section, sIdx) => (
            <motion.div key={section.id} {...enter(sIdx)} className="space-y-3">
              {/* Título da Seção */}
              <div className="flex items-center justify-between px-1">
                <h2 className="flex items-center gap-2 text-sm font-semibold tracking-tight text-foreground font-display">
                  <span className={panelAccentDot} aria-hidden />
                  <span>{section.title}</span>
                  <span className="text-xs font-normal text-muted-foreground tabular-nums">
                    ({section.events.length})
                  </span>
                </h2>

                {section.isOrg && (
                  <Link
                    to={`/produtor/organizacoes/${section.id}/membros`}
                    className="flex items-center gap-1 text-xs font-medium text-primary hover:opacity-80 transition-opacity"
                  >
                    <Users className="h-3.5 w-3.5" />
                    <span>{t("events.sections.team")}</span>
                  </Link>
                )}
              </div>

              {/* Linhas de Evento em panelRow */}
              <div className="space-y-2.5">
                {section.events.map((event) => {
                  const nonGuestTiers =
                    event.ticket_tiers?.filter(
                      (t) => t.name !== "Lista de Convidado",
                    ) || [];
                  const totalSold =
                    nonGuestTiers.reduce(
                      (s: number, t) =>
                        s + (t.quantity_sold || 0) + (t.quantity_reserved || 0),
                      0,
                    ) || 0;
                  const totalCapacity =
                    nonGuestTiers.reduce(
                      (s: number, t) => s + (t.quantity_total || 0),
                      0,
                    ) || 0;
                  const occupancy =
                    totalCapacity > 0
                      ? Math.min(100, Math.round((totalSold / totalCapacity) * 100))
                      : 0;
                  const eventTimeZone = resolveEventTimeZone(event.time_zone);
                  const formattedDate = formatEventDateTimeRange(
                    event.start_date,
                    event.end_date,
                    eventTimeZone,
                    { dateStyle: "short", includeZoneLabel: false },
                  );

                  return (
                    <div
                      key={event.id}
                      onClick={() => handleCardClick(event)}
                      className={cn(
                        panelRow,
                        "group relative flex items-center gap-3.5 sm:gap-5 p-3.5 sm:p-4 cursor-pointer transition-all duration-150 hover:border-border hover:bg-secondary/20 active:scale-[0.99]",
                      )}
                    >
                      {/* Miniatura do Evento */}
                      <EventImage
                        src={event.cover_image_url}
                        alt={event.title}
                        className="h-16 w-16 sm:h-20 sm:w-28 rounded-2xl object-cover shrink-0 border border-border/40"
                      />

                      {/* Informações Centrais */}
                      <div className="min-w-0 flex-1 space-y-1.5">
                        <div className="flex flex-wrap items-center gap-2 pr-8">
                          <h3 className="font-display font-semibold text-[15px] sm:text-base text-foreground truncate group-hover:text-primary transition-colors">
                            {event.title}
                          </h3>
                          <EventStatusBadge
                            status={event.status}
                            visibility={event.visibility}
                          />
                        </div>

                        <p className="flex items-center gap-1.5 text-xs text-muted-foreground truncate">
                          <Calendar className="h-3.5 w-3.5 shrink-0" />
                          <span>{formattedDate}</span>
                          {event.venue_city && (
                            <>
                              <span>·</span>
                              <MapPin className="h-3.5 w-3.5 shrink-0" />
                              <span>{event.venue_city}</span>
                            </>
                          )}
                        </p>

                        {/* Barra de Ocupação */}
                        <div className="space-y-1 pt-0.5 max-w-xs">
                          <Progress value={occupancy} className="h-1.5 rounded-full" />
                          <p className="text-[11px] text-muted-foreground tabular-nums">
                            {t("events.card.soldStatus", {
                              sold: totalSold,
                              capacity: totalCapacity,
                              occupancy,
                            })}
                          </p>
                        </div>
                      </div>

                      {/* Ações / Abrir Painel */}
                      <div className="hidden sm:flex items-center gap-2">
                        <span className="flex h-9 w-9 items-center justify-center rounded-full border border-border/70 bg-card text-muted-foreground transition-transform duration-150 group-hover:text-foreground group-hover:border-border group-hover:translate-x-0.5">
                          <ArrowUpRight className="h-4 w-4" />
                        </span>
                      </div>

                      {/* Excluir Evento (se permitido) */}
                      {canUserDelete(event) && (
                        <button
                          type="button"
                          aria-label="Excluir evento"
                          className="absolute right-3.5 top-3.5 sm:top-auto sm:bottom-3.5 flex h-7 w-7 items-center justify-center rounded-full border border-transparent text-muted-foreground/60 hover:text-destructive hover:border-destructive/30 hover:bg-destructive/10 transition-colors z-10"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEventToDelete(event);
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Modal de Exclusão de Evento */}
      {eventToDelete && (
        <ForceDeleteEventDialog
          eventTitle={eventToDelete.title}
          open={!!eventToDelete}
          onOpenChange={(open) => {
            if (!open) setEventToDelete(null);
          }}
          onConfirm={() =>
            deleteMutation.mutateAsync({
              eventId: eventToDelete.id,
              producerId: user!.id,
            })
          }
        />
      )}

      {/* Botão Flutuante Mobile (FAB) */}
      <Button
        asChild
        className="fixed bottom-6 right-6 h-12 w-12 rounded-full z-50 md:hidden bg-primary text-primary-foreground shadow-lg flex items-center justify-center p-0"
      >
        <Link to="/produtor/eventos/novo">
          <Plus className="h-6 w-6" />
        </Link>
      </Button>
    </div>
  );
}
